import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Crosshair, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  sitesApi, CreateSiteBody, GeofenceMode, SiteStatus, WfhPolicy,
} from "@/lib/sites-api";
import { departmentsApi, Department, departmentLabel } from "@/lib/departments-api";
import { settingsApi } from "@/lib/settings-api";
import { ApiError } from "@/lib/api";
import { validateRequired } from "@/lib/form-validation";
import { Checkbox } from "@/components/ui/checkbox";
import { Field } from "@/components/ui/field";
import { GeofenceMapPicker } from "@/components/GeofenceMapPicker";

const MODE_OPTIONS: { value: GeofenceMode; label: string; hint: string }[] = [
  { value: "GPS_AND_GEOFENCE", label: "GPS + Geofence", hint: "Employee must be inside the circle to clock in/out." },
  { value: "GPS_ONLY", label: "GPS Only", hint: "Record GPS location without enforcing the geofence." },
  { value: "MANUAL", label: "Manual", hint: "Supervisor marks attendance manually." },
];

const WFH_OPTIONS: { value: WfhPolicy; label: string; hint: string }[] = [
  { value: "INHERIT", label: "Inherit tenant setting", hint: "Follows the WFH switch in Settings → Geofencing." },
  { value: "ALLOW", label: "Allow WFH", hint: "Employees based at this site may check in from their home location." },
  { value: "DENY", label: "Deny WFH", hint: "Employees based at this site must check in on-site, even if the tenant allows WFH." },
];

const STATUS_OPTIONS: { value: SiteStatus; label: string }[] = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

const emptyForm: CreateSiteBody = {
  code: "",
  name: "",
  address: "",
  latitude: 3.1569,
  longitude: 101.7123,
  geofenceRadiusM: null,
  geofenceMode: "GPS_AND_GEOFENCE",
  wfhPolicy: "INHERIT",
  departmentIds: [],
  status: "ACTIVE",
  workingDays: [],
  state: null,
  workStartTime: null,
  workEndTime: null,
  gracePeriodMins: null,
  lateThresholdMins: null,
  minWorkHoursPerDay: null,
};

const SiteForm = () => {
  const params = useParams<{ id: string }>();
  const editingId = params.id;
  const isEdit = !!editingId;
  const navigate = useNavigate();

  const [form, setForm] = useState<CreateSiteBody>(emptyForm);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [tenantDefaultRadius, setTenantDefaultRadius] = useState<number | null>(null);

  useEffect(() => {
    departmentsApi.listAll().then(setDepartments).catch(() => { /* non-fatal */ });
    // The organisation's default radius, so a site that inherits can show the
    // number that will actually apply rather than a hardcoded guess. Non-fatal:
    // without it the hint falls back to 150 and saving still behaves correctly.
    settingsApi.get().then((cfg) => setTenantDefaultRadius(cfg.defaultGeofenceRadiusM)).catch(() => { /* non-fatal */ });
  }, []);

  useEffect(() => {
    if (!editingId) return;
    let cancelled = false;
    setLoading(true);
    sitesApi
      .get(editingId)
      .then((s) => {
        if (cancelled) return;
        setForm({
          code: s.code,
          name: s.name,
          address: s.address ?? "",
          latitude: s.latitude,
          longitude: s.longitude,
          geofenceRadiusM: s.geofenceRadiusM,
          geofenceMode: s.geofenceMode,
          wfhPolicy: s.wfhPolicy,
          departmentIds: s.departments.map((d) => d.id),
          status: s.status,
          workingDays: s.workingDays ?? [],
          state: s.state,
          workStartTime: s.workStartTime,
          workEndTime: s.workEndTime,
          gracePeriodMins: s.gracePeriodMins,
          lateThresholdMins: s.lateThresholdMins,
          minWorkHoursPerDay: s.minWorkHoursPerDay,
        });
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(e instanceof ApiError ? e.message : "Failed to load site");
        navigate("/sites");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [editingId, navigate]);

  const update = <K extends keyof CreateSiteBody>(key: K, value: CreateSiteBody[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported in this browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm((prev) => ({ ...prev, latitude: pos.coords.latitude, longitude: pos.coords.longitude })),
      (err) => toast.error(err.message || "Could not get current location"),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const submit = async () => {
    const missing = validateRequired([
      { label: "Site code", value: form.code },
      { label: "Site name", value: form.name },
    ]);
    if (missing) {
      toast.error(missing);
      return;
    }
    setSubmitting(true);
    try {
      const payload: CreateSiteBody = {
        ...form,
        code: form.code.trim(),
        name: form.name.trim(),
        address: form.address?.trim() || undefined,
      };
      if (isEdit && editingId) {
        await sitesApi.update(editingId, payload);
        toast.success(`Site ${form.name} updated`);
      } else {
        await sitesApi.create(payload);
        toast.success(`Site ${form.name} created`);
      }
      navigate("/sites");
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Failed to save site");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const inherited = tenantDefaultRadius ?? 150;
  const inheritsRadius = form.geofenceRadiusM == null;
  const radius = form.geofenceRadiusM ?? inherited;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/sites"><ArrowLeft className="mr-1.5 h-4 w-4" />Back to sites</Link>
        </Button>
        <div>
          <h1 className="text-xl font-semibold">{isEdit ? `Edit site · ${form.name || ""}` : "Add new site"}</h1>
          <p className="text-xs text-muted-foreground">
            {isEdit ? "Update site details, geofence and status." : "Configure a new work location with geofencing rules."}
          </p>
        </div>
      </div>

      <div className="grid items-stretch gap-5 lg:grid-cols-[minmax(0,1fr)_400px]">
        {/* Left: Map (fills full column height) */}
        <Card className="flex flex-col overflow-hidden p-4">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Location & geofence</div>
              <div className="text-xs text-muted-foreground">Search, click the map, or drag the pin to set the center.</div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={useMyLocation} className="h-9">
              <Crosshair className="mr-1.5 h-3.5 w-3.5" />Use my location
            </Button>
          </div>
          {/* The map is the flex item itself: it takes whatever height the card
              has spare, and never less than 420px. Wrapped in a div of its own,
              the wrapper grew while the map stayed a fixed 288px, leaving a
              large empty band above the coordinates. */}
          <GeofenceMapPicker
            className="min-h-[420px] flex-1"
            latitude={form.latitude}
            longitude={form.longitude}
            radiusM={radius}
            onChange={({ latitude, longitude }) => setForm((prev) => ({ ...prev, latitude, longitude }))}
          />
          <div className="mt-4 grid grid-cols-[1fr_1fr_minmax(0,2fr)] items-end gap-4">
            <Field label="Latitude">
              <Input
                type="number"
                step="any"
                value={form.latitude}
                onChange={(e) => update("latitude", Number(e.target.value))}
              />
            </Field>
            <Field label="Longitude">
              <Input
                type="number"
                step="any"
                value={form.longitude}
                onChange={(e) => update("longitude", Number(e.target.value))}
              />
            </Field>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Geofence radius</Label>
                <span className="text-xs font-mono text-muted-foreground">
                  {radius}m{inheritsRadius ? " · inherited" : ""}
                </span>
              </div>
              <Slider
                value={[radius]}
                onValueChange={(v) => update("geofenceRadiusM", v[0])}
                disabled={inheritsRadius}
                min={10}
                max={1000}
                step={10}
                className="mt-2"
              />
              <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                <Checkbox
                  checked={inheritsRadius}
                  onCheckedChange={(c) => update("geofenceRadiusM", c === true ? null : inherited)}
                />
                Use the organisation default ({inherited}m)
              </label>
            </div>
          </div>
        </Card>

        {/* Right: Details */}
        <Card className="p-4">
          <div className="mb-3">
            <div className="text-sm font-medium">Site details</div>
            <div className="text-xs text-muted-foreground">Identify this location for your team.</div>
          </div>
          <div className="space-y-4">
            <Field label="Site name">
              <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Main Warehouse" />
            </Field>
            <Field label="Site code">
              <Input value={form.code} onChange={(e) => update("code", e.target.value)} placeholder="WH-01" />
            </Field>
            <Field label="Address">
              <Input
                value={form.address ?? ""}
                onChange={(e) => update("address", e.target.value)}
                placeholder="Street, city, postcode"
              />
            </Field>
            <Field
              label="State / region"
              hint="Optional label. Helps pick the right sites when adding a state public holiday; it does not affect any rule."
            >
              <Input
                value={form.state ?? ""}
                onChange={(e) => update("state", e.target.value || null)}
                placeholder="Selangor"
              />
            </Field>
            <Field label="Attendance mode">
              <Select
                value={form.geofenceMode ?? "GPS_AND_GEOFENCE"}
                onValueChange={(v) => update("geofenceMode", v as GeofenceMode)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {MODE_OPTIONS.find((m) => m.value === form.geofenceMode)?.hint}
              </p>
            </Field>
            <Field label="Work from home">
              <Select
                value={form.wfhPolicy ?? "INHERIT"}
                onValueChange={(v) => update("wfhPolicy", v as WfhPolicy)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WFH_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {WFH_OPTIONS.find((m) => m.value === (form.wfhPolicy ?? "INHERIT"))?.hint}
              </p>
            </Field>
            <Field label="Status">
              <Select
                value={form.status ?? "ACTIVE"}
                onValueChange={(v) => update("status", v as SiteStatus)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Departments">
              {departments.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  No departments yet — create them on the Departments page first.
                </p>
              ) : (
                <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-md border p-2.5">
                  {departments.map((d) => {
                    const checked = (form.departmentIds ?? []).includes(d.id);
                    return (
                      <label key={d.id} className="flex cursor-pointer items-center gap-2 text-sm">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(on) => {
                            const current = form.departmentIds ?? [];
                            update(
                              "departmentIds",
                              on ? [...current, d.id] : current.filter((id) => id !== d.id),
                            );
                          }}
                        />
                        <span className="truncate">{departmentLabel(d)}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              <p className="mt-1 text-[11px] text-muted-foreground">
                Which units operate at this site — pick at any level. A site can serve several.
              </p>
            </Field>
          </div>
        </Card>
      </div>

      {/* Optional per-site attendance overrides */}
      <AttendanceOverridesCard form={form} update={update} />

      <div className="sticky bottom-0 -mx-4 border-t bg-background/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={() => navigate("/sites")} disabled={submitting}>Cancel</Button>
          <Button className="bg-gradient-primary" onClick={submit} disabled={submitting}>
            {submitting
              ? <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />{isEdit ? "Saving…" : "Creating…"}</>
              : (isEdit ? "Save changes" : "Create site")}
          </Button>
        </div>
      </div>
    </div>
  );
};


type UpdateFn = <K extends keyof CreateSiteBody>(key: K, value: CreateSiteBody[K]) => void;

/**
 * Per-site attendance overrides. Each override is toggled on/off — when off,
 * the field is cleared (sent as `null` to the API) and the tenant default is
 * used. When on, the admin picks a site-specific value.
 */
const AttendanceOverridesCard = ({
  form,
  update,
}: {
  form: CreateSiteBody;
  update: UpdateFn;
}) => {
  const hasAnyOverride =
    (form.workingDays?.length ?? 0) > 0 ||
    form.workStartTime != null ||
    form.workEndTime != null ||
    form.gracePeriodMins != null ||
    form.lateThresholdMins != null ||
    form.minWorkHoursPerDay != null;

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-medium">Attendance rule overrides (optional)</div>
          <div className="text-xs text-muted-foreground">
            Leave these off to inherit your organization's defaults from Settings.
            Enable any one if this site runs different hours or tolerances.
          </div>
        </div>
        {hasAnyOverride && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={() => {
              update("workingDays", []);
              update("workStartTime", null);
              update("workEndTime", null);
              update("gracePeriodMins", null);
              update("lateThresholdMins", null);
              update("minWorkHoursPerDay", null);
            }}
          >
            Reset all to inherit
          </Button>
        )}
      </div>

      {/* Working week (FEAT-2026-017). Per-site because an organisation can
          span states with different weekends -- Johor, Kedah, Kelantan and
          Terengganu run Friday-Saturday. Nothing selected inherits the
          organisation's week; it never means "no working days". */}
      <div className="mb-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium">Working days</span>
          <span className="text-[11px] text-muted-foreground">
            {(form.workingDays?.length ?? 0) === 0
              ? "Inheriting the organisation's week"
              : "Overriding the organisation"}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {[
            { n: 1, l: "Mon" }, { n: 2, l: "Tue" }, { n: 3, l: "Wed" },
            { n: 4, l: "Thu" }, { n: 5, l: "Fri" }, { n: 6, l: "Sat" },
            { n: 7, l: "Sun" },
          ].map((d) => {
            const days = form.workingDays ?? [];
            const on = days.includes(d.n);
            return (
              <button
                key={d.n}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  update(
                    "workingDays",
                    (on ? days.filter((x) => x !== d.n) : [...days, d.n]).sort((a, b) => a - b),
                  )
                }
                className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                  on
                    ? "border-primary bg-primary/10 font-medium text-foreground"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {d.l}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Employees based here are not chased for missed attendance on other days.
          Select nothing to follow the organisation's working week.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <OverrideTimeField
          label="Work start time"
          hint="Default from Settings if off"
          value={form.workStartTime ?? null}
          placeholder="09:00"
          onChange={(v) => update("workStartTime", v)}
        />
        <OverrideTimeField
          label="Work end time"
          hint="Default from Settings if off"
          value={form.workEndTime ?? null}
          placeholder="18:00"
          onChange={(v) => update("workEndTime", v)}
        />
        <OverrideNumberField
          label="Grace period (mins)"
          hint="Late check-in tolerance"
          value={form.gracePeriodMins ?? null}
          placeholder="10"
          min={0}
          max={240}
          onChange={(v) => update("gracePeriodMins", v)}
        />
        <OverrideNumberField
          label="Late threshold (mins)"
          hint="When to flag as late"
          value={form.lateThresholdMins ?? null}
          placeholder="15"
          min={0}
          max={240}
          onChange={(v) => update("lateThresholdMins", v)}
        />
        <OverrideNumberField
          label="Min work hours per day"
          hint="For partial-day warnings"
          value={form.minWorkHoursPerDay ?? null}
          placeholder="8"
          min={0}
          max={24}
          step={0.25}
          onChange={(v) => update("minWorkHoursPerDay", v)}
        />
      </div>
    </Card>
  );
};

const OverrideTimeField = ({
  label,
  hint,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  hint: string;
  value: string | null;
  placeholder: string;
  onChange: (v: string | null) => void;
}) => {
  const enabled = value !== null;
  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-xs font-medium">{label}</Label>
          <div className="text-[11px] text-muted-foreground">
            {enabled ? "Override active" : hint}
          </div>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(on) => onChange(on ? placeholder : null)}
        />
      </div>
      {enabled && (
        <Input
          type="time"
          value={value}
          className="mt-2"
          onChange={(e) => onChange(e.target.value || null)}
        />
      )}
    </div>
  );
};

const OverrideNumberField = ({
  label,
  hint,
  value,
  placeholder,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  hint: string;
  value: number | null;
  placeholder: string;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number | null) => void;
}) => {
  const enabled = value !== null;
  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-xs font-medium">{label}</Label>
          <div className="text-[11px] text-muted-foreground">
            {enabled ? "Override active" : hint}
          </div>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(on) => onChange(on ? Number(placeholder) : null)}
        />
      </div>
      {enabled && (
        <Input
          type="number"
          min={min}
          max={max}
          step={step ?? 1}
          value={value}
          className="mt-2"
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") return; // keep override on, just empty input
            const n = Number(raw);
            if (!Number.isNaN(n)) onChange(n);
          }}
        />
      )}
    </div>
  );
};

export default SiteForm;
